import { useRef, useState, useEffect, useContext } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { AlternativeWithoutExisting } from "src/helpers/types";
import { isMobile } from "src/helpers/mapConstants";

import nbOnly from "../lib/geojson/alternatives/NB_Only.json";
import sbOnly from "../lib/geojson/alternatives/SB_Only.json";
import convergingDiverging from "../lib/geojson/alternatives/Converging_Diverging.json";
import convergingDivergingShort from "../lib/geojson/alternatives/Converging_Diverging_Short.json";
import AnimatedMapLine from "./AnimatedMapLine";
import { LanguageContext } from "./languageContext";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN as string;

const alternativeToGeoJson: Record<
  AlternativeWithoutExisting,
  GeoJSON.FeatureCollection
> = {
  NB_Only: nbOnly as GeoJSON.FeatureCollection,
  SB_Only: sbOnly as GeoJSON.FeatureCollection,
  Converging_Diverging: convergingDiverging as GeoJSON.FeatureCollection,
  Converging_Diverging_Short:
    convergingDivergingShort as GeoJSON.FeatureCollection,
};

export default function AlternativesExplanationMap({
  alternative,
}: {
  alternative: AlternativeWithoutExisting;
}) {
  const { translate } = useContext(LanguageContext);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [lng] = useState<number>(-122.42117318230964);
  const [lat] = useState<number>(37.75737983593204);
  const [zoom] = useState<number>(15);
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);

  const sourceName = `VALENCIA_LANES_${alternative}`;
  const sbLanesLayerId = `${alternative}-sb-lanes`;
  const nbLanesLayerId = `${alternative}-nb-lanes`;

  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [lng, lat],
      zoom: zoom,
      cooperativeGestures: isMobile,
    });

    map.current.addControl(
      new mapboxgl.NavigationControl({ showCompass: false })
    );

    map.current.on("load", () => {
      if (!map.current) return;

      setIsMapLoaded(true);

      map.current.addSource(sourceName, {
        type: "geojson",
        data: alternativeToGeoJson[alternative],
      });

      const layers = [
        { color: "#067cd0", direction: "SB", id: sbLanesLayerId },
        { color: "black", direction: "NB", id: nbLanesLayerId },
      ];

      layers.forEach(({ color, direction, id }) => {
        map.current?.addLayer?.({
          id,
          type: "line",
          source: sourceName,
          filter: ["==", ["get", "direction"], direction],
          paint: {
            "line-color": color,
            "line-width": 4,
            "line-dasharray": [4, 1],
          },
        });

        map.current?.on("click", id, (e) => {
          if (!e.features?.[0]) return;

          const coordinates = e.lngLat;
          const direction = e.features[0].properties?.direction;

          if (direction) {
            new mapboxgl.Popup()
              .setLngLat(coordinates)
              .addClassName("mapboxgl-popup-no-aria-hidden")
              .setHTML(
                `<p>${
                  direction === "SB"
                    ? translate("southbound")
                    : translate("northbound")
                } ${translate("valenciaStreet")}</p>`
              )
              .addTo(map.current!);
          }
        });

        map.current?.on("mouseenter", id, () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = "pointer";
          }
        });

        map.current?.on("mouseleave", id, () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = "";
          }
        });
      });
    });

    const removeLayers = () => {
      if (map.current?.getLayer(sbLanesLayerId)) {
        map.current?.removeLayer(sbLanesLayerId);
      }
      if (map.current?.getLayer(nbLanesLayerId)) {
        map.current?.removeLayer(nbLanesLayerId);
      }
      if (map.current?.getSource(sourceName)) {
        map.current?.removeSource(sourceName);
      }
    };
    removeLayers();
    setIsMapLoaded(false);

    return () => {
      map.current?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alternative]);

  return (
    <>
      {isMapLoaded && (
        <>
          <AnimatedMapLine id={sbLanesLayerId} map={map} />
          <AnimatedMapLine id={nbLanesLayerId} map={map} />
        </>
      )}
      <div ref={mapContainer} className="h-[500px] w-full" aria-hidden="true" />
    </>
  );
}
